import React from 'react'

import type { useFormik } from '@fourthwall/utils/dist/formik'
import type { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'

import type { FormValues, Rules } from '../../hooks/useBadgeForm/types'
import * as S from './styled'

type Props = {
  form: ReturnType<typeof useFormik<FormValues>>
  tiers?: NonNullable<ReturnType<typeof useTiersQuery>['tiersQuery']['data']>['tiers']
}

export const Summary = ({ form, tiers }: Props) => {
  if (!form.values.rules.length) {
    return 'Enter information on the left to see a summary of your rules here.'
  }

  const renderRule = (rule: Rules[number]) => {
    if (rule.type === 'CommentsWritten') {
      if (form.values.commentsWrittenMin && form.values.commentsWrittenMax) {
        return `Wrote ${form.values.commentsWrittenMin} to ${form.values.commentsWrittenMax} comments`
      }

      if (form.values.commentsWrittenMin) {
        return `Wrote at least ${form.values.commentsWrittenMin} comments`
      }

      return null
    }

    if (rule.type === 'LikesGiven') {
      if (form.values.likesGivenMin && form.values.likesGivenMax) {
        return `Gave ${form.values.likesGivenMin} to ${form.values.likesGivenMax} likes`
      }

      if (form.values.likesGivenMin) {
        return `Gave ${form.values.likesGivenMin} likes`
      }

      return null
    }

    if (rule.type === 'LikesReceived') {
      if (form.values.likesReceivedMin && form.values.likesReceivedMax) {
        return `Received ${form.values.likesReceivedMin} to ${form.values.likesReceivedMax} likes`
      }

      if (form.values.likesReceivedMin) {
        return `Received ${form.values.likesReceivedMin} likes`
      }

      return null
    }

    if (rule.type === 'GiftsGiven') {
      if (form.values.gifsGivenMin && form.values.gifsGivenMax) {
        return `Gifted ${form.values.gifsGivenMin} to ${form.values.gifsGivenMax} subscriptions`
      }

      if (form.values.gifsGivenMin) {
        return `Gifted ${form.values.gifsGivenMin} subscriptions`
      }

      return null
    }

    if (rule.type === 'FirstSubscribedAt') {
      if (form.values.firstSubscribedAtFrom && form.values.firstSubscribedAtTo) {
        return (
          <>
            Subscribed from{' '}
            <TimezonedDateString
              date={form.values.firstSubscribedAtFrom}
              format="MMM D, YYYY, h:mm A"
              withTimezoneAbbreviation={false}
            />{' '}
            until{' '}
            <TimezonedDateString
              date={form.values.firstSubscribedAtTo}
              format="MMM D, YYYY, h:mm A"
              withTimezoneAbbreviation={false}
            />
          </>
        )
      }

      if (form.values.firstSubscribedAtFrom) {
        return (
          <>
            Subscribed from{' '}
            <TimezonedDateString
              date={form.values.firstSubscribedAtFrom}
              format="MMM D, YYYY, h:mm A"
              withTimezoneAbbreviation={false}
            />
          </>
        )
      }

      if (form.values.firstSubscribedAtTo) {
        return (
          <>
            Subscribed until{' '}
            <TimezonedDateString
              date={form.values.firstSubscribedAtTo}
              format="MMM D, YYYY, h:mm A"
              withTimezoneAbbreviation={false}
            />
          </>
        )
      }

      return null
    }

    if (rule.type === 'ActiveMemberAt') {
      if (form.values.activeMemberAtFrom || form.values.activeMemberAtTo) {
        return `Member active during selected period`
      }

      return null
    }

    if (rule.type === 'Tier') {
      if (rule.tierId) {
        const tier = tiers?.find((tier) => tier.id === rule.tierId)

        return `${tier ? tier.name : rule.tierId} members`
      }

      return null
    }

    return null
  }

  return (
    <S.List>
      {form.values.rules.map((rule) => {
        const ruleCopy = renderRule(rule)

        if (!ruleCopy) return null

        return <li key={rule.type}>{ruleCopy}</li>
      })}
      {form.values.removeWhenRulesNoLongerMet && (
        <li>Remove badge when requirements are no longer met</li>
      )}
    </S.List>
  )
}
